//frontend/src/pages/gro/RiskInventoryDetailPage.tsx
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Pencil, Plus } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { RiskInventoryAiInsightsPanel } from "@/components/gro/RiskInventoryAiInsightsPanel";
import { RiskInventoryItemForm } from "@/components/gro/RiskInventoryItemForm";
import { RiskInventoryItemsModerationTable } from "@/components/gro/RiskInventoryItemsModerationTable";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { EmptyState } from "@/components/shared/EmptyState";
import { PageHeader } from "@/components/shared/PageHeader";
import { PaginationFooter } from "@/components/shared/PaginationFooter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useUnits } from "@/hooks/useUnits";
import { useAuth } from "@/contexts/AuthContext";
import {
  useCreateRiskInventoryItem,
  useDeleteRiskInventoryItem,
  useRiskInventoryDetail,
  useRiskInventoryItemFormOptions,
  useRiskInventoryItemLogs,
  useRiskInventoryItems,
  useRemoveRiskInventoryItemFromInventory,
  useSetRiskInventoryItemStatus,
  useUpdateRiskInventory,
  useUpdateRiskInventoryItem,
} from "@/hooks/useRiskInventories";
import { PERMISSIONS } from "@/lib/permissions";
import type {
  RiskInventoryItem,
  RiskInventoryItemCreateIn,
  RiskInventoryItemLog,
  RiskInventoryItemStatus,
  RiskInventoryItemUpdateIn,
} from "@/types/riskInventory";

const DEFAULT_ITEM_FORM: RiskInventoryItemCreateIn = {
  title: "",
  description: "",
  risk_catalog_id: "",
  dimension_id: "",
  campaign_id: null,
  unit_id: null,
  department_id: null,
  job_position_id: null,
  worker_group_id: null,
  probability_level_id: "",
  severity_level_id: "",
};

type StatusDialogMode = "mitigated" | "closed" | "open" | null;

type InventoryEditForm = {
  name: string;
  version_label: string;
  unit_id: string | null;
};

const DEFAULT_INVENTORY_EDIT_FORM: InventoryEditForm = {
  name: "",
  version_label: "",
  unit_id: null,
};

function mapItemToForm(item: RiskInventoryItem): RiskInventoryItemUpdateIn {
  return {
    title: item.title || "",
    description: item.description || "",
    risk_catalog_id: item.risk_catalog_id || "",
    dimension_id: item.dimension_id || "",
    campaign_id: item.campaign_id || null,
    unit_id: item.unit_id || null,
    department_id: item.department_id || null,
    job_position_id: item.job_position_id || null,
    worker_group_id: item.worker_group_id || null,
    probability_level_id: "",
    severity_level_id: "",
  };
}

function formatDateTime(value?: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);
}

function actionLabel(value?: string | null) {
  const normalized = (value || "").trim().toLowerCase();
  if (!normalized) return "-";
  if (normalized.includes("criou")) return "Criou";
  if (normalized.includes("editou")) return "Editou";
  if (normalized.includes("mitigou")) return "Mitigou";
  if (normalized.includes("concluiu")) return "Concluiu";
  if (normalized.includes("reabriu")) return "Reabriu";
  if (normalized.includes("excluiu")) return "Excluiu";
  if (normalized.includes("removeu")) return "Removeu do inventário";
  return value || "-";
}

function renderLogCell(value?: string | null) {
  return value?.trim() ? value : "-";
}

export default function RiskInventoryDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();

  const canRead = user?.permissions?.includes(PERMISSIONS.RISK_INVENTORY_READ) ?? false;
  const canWrite = user?.permissions?.includes(PERMISSIONS.RISK_INVENTORY_WRITE) ?? false;

  const [statusFilter, setStatusFilter] = useState<"all" | RiskInventoryItemStatus | "without_action_plan">("all");
  const [dimensionId, setDimensionId] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<10 | 20 | 30>(10);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [itemDialogMode, setItemDialogMode] = useState<"create" | "edit">("create");
  const [formValue, setFormValue] = useState<RiskInventoryItemCreateIn | RiskInventoryItemUpdateIn>(DEFAULT_ITEM_FORM);
  const [selectedItem, setSelectedItem] = useState<RiskInventoryItem | null>(null);
  const [inventoryEditOpen, setInventoryEditOpen] = useState(false);
  const [inventoryEditForm, setInventoryEditForm] = useState<InventoryEditForm>(DEFAULT_INVENTORY_EDIT_FORM);
  const [logsItem, setLogsItem] = useState<RiskInventoryItem | null>(null);
  const [logsOpen, setLogsOpen] = useState(false);
  const [statusDialogMode, setStatusDialogMode] = useState<StatusDialogMode>(null);
  const [technicalOpinion, setTechnicalOpinion] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<RiskInventoryItem | null>(null);
  const [removeTarget, setRemoveTarget] = useState<RiskInventoryItem | null>(null);

  const detailQ = useRiskInventoryDetail(id);
  const itemsQ = useRiskInventoryItems(id, {
    status: statusFilter === "all" ? undefined : statusFilter,
    dimension_id: dimensionId === "all" ? undefined : dimensionId,
    page,
    per_page: perPage,
  });
  const formOptionsQ = useRiskInventoryItemFormOptions(id);
  const logsQ = useRiskInventoryItemLogs(logsItem?.id, logsOpen);
  const unitsQ = useUnits();

  const createItemMut = useCreateRiskInventoryItem();
  const updateItemMut = useUpdateRiskInventoryItem();
  const setStatusMut = useSetRiskInventoryItemStatus();
  const updateInventoryMut = useUpdateRiskInventory();
  const deleteItemMut = useDeleteRiskInventoryItem();
  const removeFromInventoryMut = useRemoveRiskInventoryItemFromInventory();

  const inventory = detailQ.data;
  const pageData = itemsQ.data;
  const items = pageData?.items ?? [];
  const formOptions = formOptionsQ.data;
  const dimensions = pageData?.dimensions ?? [];
  const logs = logsQ.data ?? [];
  const units = unitsQ.data ?? [];

  const busy = useMemo(
    () =>
      createItemMut.isPending ||
      updateItemMut.isPending ||
      setStatusMut.isPending ||
      updateInventoryMut.isPending ||
      deleteItemMut.isPending ||
      removeFromInventoryMut.isPending,
    [
      createItemMut.isPending,
      updateItemMut.isPending,
      setStatusMut.isPending,
      updateInventoryMut.isPending,
      deleteItemMut.isPending,
      removeFromInventoryMut.isPending,
    ],
  );

  useEffect(() => {
    setPage(1);
  }, [statusFilter, dimensionId, perPage]);

  useEffect(() => {
    if (!inventory) return;
    setInventoryEditForm({
      name: inventory.name || "",
      version_label: inventory.version_label || "",
      unit_id: inventory.unit_id || null,
    });
  }, [inventory]);

  function openCreateDialog() {
    setSelectedItem(null);
    setItemDialogMode("create");
    setFormValue(DEFAULT_ITEM_FORM);
    setItemDialogOpen(true);
  }

  function openEditDialog(item: RiskInventoryItem) {
    const probabilityMatch = formOptions?.probability_levels.find(
      (option) => option.name === item.probability_level || Number(option.score) === Number(item.probability_score),
    );
    const severityMatch = formOptions?.severity_levels.find(
      (option) => option.name === item.severity_level || Number(option.score) === Number(item.severity_score),
    );

    setSelectedItem(item);
    setItemDialogMode("edit");
    setFormValue({
      ...mapItemToForm(item),
      probability_level_id: probabilityMatch?.id || "",
      severity_level_id: severityMatch?.id || "",
    });
    setItemDialogOpen(true);
  }

  function openLogs(item: RiskInventoryItem) {
    setLogsItem(item);
    setLogsOpen(true);
  }

  async function handleSaveInventory() {
    if (!id) return;
    if (!inventoryEditForm.name.trim()) {
      toast.error("Informe o nome do inventário.");
      return;
    }

    try {
      await updateInventoryMut.mutateAsync({
        inventoryId: id,
        body: {
          name: inventoryEditForm.name.trim(),
          version_label: inventoryEditForm.version_label.trim() || null,
          unit_id: inventoryEditForm.unit_id || null,
        },
      });
      toast.success("Inventário atualizado com sucesso.");
      setInventoryEditOpen(false);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao atualizar inventário.");
    }
  }

  async function handleSubmitItem() {
    if (!id) return;
    if (
      !formValue.dimension_id ||
      !formValue.risk_catalog_id ||
      !formValue.probability_level_id ||
      !formValue.severity_level_id ||
      !formValue.title.trim()
    ) {
      toast.error("Preencha os campos obrigatórios do item do inventário.");
      return;
    }

    try {
      if (itemDialogMode === "create") {
        await createItemMut.mutateAsync({ inventoryId: id, body: { ...formValue, title: formValue.title.trim() } });
        toast.success("Item do inventário criado com sucesso.");
      } else if (selectedItem) {
        await updateItemMut.mutateAsync({
          itemId: selectedItem.id,
          inventoryId: id,
          body: { ...formValue, title: formValue.title.trim() },
        });
        toast.success("Item do inventário atualizado com sucesso.");
      }
      setItemDialogOpen(false);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar item do inventário.");
    }
  }

  function openStatusDialog(item: RiskInventoryItem, nextStatus: Exclude<StatusDialogMode, null>) {
    setSelectedItem(item);
    setStatusDialogMode(nextStatus);
    setTechnicalOpinion("");
  }

  async function confirmStatusChange() {
    if (!selectedItem || !statusDialogMode || !id) return;
    if (technicalOpinion.trim().length < 3) {
      toast.error("Informe um parecer técnico com pelo menos 3 caracteres.");
      return;
    }

    try {
      await setStatusMut.mutateAsync({
        itemId: selectedItem.id,
        inventoryId: id,
        status: statusDialogMode,
        technicalOpinion: technicalOpinion.trim(),
      });
      toast.success("Status do item atualizado com sucesso.");
      setStatusDialogMode(null);
      setTechnicalOpinion("");
      setSelectedItem(null);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao atualizar status do item.");
    }
  }

  async function confirmDelete() {
    if (!deleteTarget || !id) return;
    try {
      await deleteItemMut.mutateAsync({ itemId: deleteTarget.id, inventoryId: id });
      toast.success("Item excluído com sucesso.");
      setDeleteTarget(null);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir item.");
    }
  }

  async function confirmRemove() {
    if (!removeTarget || !id) return;
    try {
      await removeFromInventoryMut.mutateAsync({ itemId: removeTarget.id, inventoryId: id });
      toast.success("Item removido do inventário com sucesso.");
      setRemoveTarget(null);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao remover item do inventário.");
    }
  }

  return (
    <div className="pb-10">
      <PageHeader
        title={inventory ? inventory.name : "Inventário de Riscos"}
        description="Gerencie os itens do inventário e mantenha a classificação manual do risco organizada."
      >
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate("/gro/risk-inventories")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
        </div>
      </PageHeader>

      {!canRead ? (
        <Card className="p-6 text-sm text-destructive">Você não possui permissão para visualizar este inventário.</Card>
      ) : detailQ.isLoading || itemsQ.isLoading || formOptionsQ.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">Carregando inventário...</Card>
      ) : detailQ.isError || itemsQ.isError || formOptionsQ.isError || !inventory || !formOptions ? (
        <Card className="p-6 text-sm text-destructive">
          {(detailQ.error as any)?.message ||
            (itemsQ.error as any)?.message ||
            (formOptionsQ.error as any)?.message ||
            "Erro ao carregar o inventário."}
        </Card>
      ) : (
        <div className="space-y-6">
          <Card className="p-4">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium">Indicadores do inventário</div>
                <div className="text-xs text-muted-foreground">Resumo rápido dos dados principais do inventário.</div>
              </div>
              {canWrite ? (
                <Button variant="outline" onClick={() => setInventoryEditOpen(true)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Editar
                </Button>
              ) : null}
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-xl border p-4">
                <div className="text-xs uppercase text-muted-foreground">Nome do inventário</div>
                <div className="mt-2 text-2xl font-semibold">{inventory.name}</div>
              </div>

              <div className="rounded-xl border p-4">
                <div className="text-xs uppercase text-muted-foreground">Versão</div>
                <div className="mt-2 text-2xl font-semibold">{inventory.version_label || "-"}</div>
              </div>

              <div className="rounded-xl border p-4">
                <div className="text-xs uppercase text-muted-foreground">Unidade</div>
                <div className="mt-2 text-2xl font-semibold">{inventory.unit_name || "-"}</div>
              </div>

              <div className="rounded-xl border p-4">
                <div className="text-xs uppercase text-muted-foreground">Total de itens do inventário</div>
                <div className="mt-2 text-2xl font-semibold">{pageData?.total ?? inventory.items_count ?? 0}</div>
              </div>
            </div>
          </Card>

          <Tabs defaultValue="items" className="space-y-4">
            <TabsList className="grid w-full grid-cols-2 lg:w-[520px]">
              <TabsTrigger value="items">Itens do inventário</TabsTrigger>
              <TabsTrigger value="ai_insights">Insights Normex Copilot</TabsTrigger>
            </TabsList>

            <TabsContent value="items" className="space-y-4">
              <Card className="p-4">
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] xl:items-end">
                  <div>
                    <div className="mb-2 text-sm font-medium">Status do item</div>
                    <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as "all" | RiskInventoryItemStatus | "without_action_plan")}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="open">Aberto</SelectItem>
                        <SelectItem value="mitigated">Mitigado</SelectItem>
                        <SelectItem value="closed">Encerrado</SelectItem>
                        <SelectItem value="without_action_plan">Sem Plano de Ação</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <div className="mb-2 text-sm font-medium">Dimensão</div>
                    <Select value={dimensionId} onValueChange={setDimensionId}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        {dimensions.map((item) => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {canWrite ? (
                    <Button onClick={openCreateDialog} disabled={!formOptions || busy} className="w-full xl:w-auto">
                      <Plus className="mr-2 h-4 w-4" />
                      Novo item
                    </Button>
                  ) : null}
                </div>
              </Card>

              {items.length ? (
                <>
                  <RiskInventoryItemsModerationTable
                    items={items}
                    inventoryName={inventory.name}
                    canWrite={canWrite}
                    busy={busy}
                    onLogs={openLogs}
                    onActionPlans={(item) => navigate(`/gro/risk-inventories/${id}/items/${item.id}/action-plans`)}
                    onEdit={openEditDialog}
                    onMitigate={(item) => openStatusDialog(item, "mitigated")}
                    onClose={(item) => openStatusDialog(item, "closed")}
                    onReopen={(item) => openStatusDialog(item, "open")}
                    onDelete={setDeleteTarget}
                    onRemoveFromInventory={setRemoveTarget}
                  />

                  <PaginationFooter
                    page={pageData?.page ?? 1}
                    perPage={perPage}
                    total={pageData?.total ?? 0}
                    totalPages={pageData?.total_pages ?? 1}
                    onPageChange={setPage}
                    onPerPageChange={setPerPage}
                    label="resultados"
                  />
                </>
              ) : (
                <EmptyState
                  title="Nenhum item encontrado"
                  description="Crie um item no inventário para começar a registrar os riscos manualmente."
                />
              )}
            </TabsContent>

            <TabsContent value="ai_insights" className="space-y-4">
              <RiskInventoryAiInsightsPanel inventoryId={id} canGenerate={canRead} />
            </TabsContent>
          </Tabs>
        </div>
      )}

      {formOptions ? (
        <RiskInventoryItemForm
          open={itemDialogOpen}
          mode={itemDialogMode}
          value={formValue}
          saving={createItemMut.isPending || updateItemMut.isPending}
          risks={formOptions.risks}
          dimensions={formOptions.dimensions}
          probabilityLevels={formOptions.probability_levels}
          severityLevels={formOptions.severity_levels}
          campaigns={formOptions.campaigns}
          units={formOptions.units}
          departments={formOptions.departments}
          jobPositions={formOptions.job_positions}
          workerGroups={formOptions.worker_groups}
          onOpenChange={setItemDialogOpen}
          onChange={setFormValue}
          onSubmit={handleSubmitItem}
        />
      ) : null}

      <Dialog open={inventoryEditOpen} onOpenChange={setInventoryEditOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar inventário</DialogTitle>
            <DialogDescription>Atualize nome, versão e unidade do inventário.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label>Nome *</Label>
              <Input
                className="mt-1"
                value={inventoryEditForm.name}
                onChange={(e) => setInventoryEditForm((current) => ({ ...current, name: e.target.value }))}
                placeholder="Ex.: Inventário de Riscos 2026"
              />
            </div>
            <div>
              <Label>Unidade</Label>
              <Select
                value={inventoryEditForm.unit_id ?? "__none__"}
                onValueChange={(value) =>
                  setInventoryEditForm((current) => ({
                    ...current,
                    unit_id: value === "__none__" ? null : value,
                  }))
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Sem unidade específica" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sem unidade específica</SelectItem>
                  {units.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id}>
                      {unit.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Versão / Rótulo</Label>
              <Input
                className="mt-1"
                value={inventoryEditForm.version_label}
                onChange={(e) => setInventoryEditForm((current) => ({ ...current, version_label: e.target.value }))}
                placeholder="Ex.: 2026.1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInventoryEditOpen(false)} disabled={updateInventoryMut.isPending}>
              Cancelar
            </Button>
            <Button onClick={handleSaveInventory} disabled={updateInventoryMut.isPending}>
              Salvar alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={logsOpen} onOpenChange={setLogsOpen}>
        <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden p-0 sm:max-w-5xl">
          <DialogHeader className="shrink-0 border-b px-6 py-4">
            <DialogTitle>
              Histórico do item do inventário{logsItem?.title ? ` • ${logsItem.title}` : ""}
            </DialogTitle>
            <DialogDescription>
              Acompanhe criação, edição, mitigação, conclusão, exclusão e remoção do inventário em formato legível.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            {logsQ.isLoading ? (
              <div className="text-sm text-muted-foreground">Carregando histórico...</div>
            ) : logsQ.isError ? (
              <div className="text-sm text-destructive">{(logsQ.error as any)?.message || "Erro ao carregar histórico."}</div>
            ) : !logs.length ? (
              <div className="text-sm text-muted-foreground">Nenhum log encontrado para este item.</div>
            ) : (
              <div className="overflow-hidden rounded-xl border bg-background">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-background">
                    <TableRow>
                      <TableHead>Data/Hora</TableHead>
                      <TableHead>Usuário</TableHead>
                      <TableHead>Ação</TableHead>
                      <TableHead>Antes</TableHead>
                      <TableHead>Depois</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log: RiskInventoryItemLog) => (
                      <TableRow key={log.id}>
                        <TableCell>{formatDateTime(log.created_at)}</TableCell>
                        <TableCell>{log.user_name || "Sistema"}</TableCell>
                        <TableCell className="font-medium">{actionLabel(log.action)}</TableCell>
                        <TableCell className="max-w-[320px] whitespace-pre-wrap text-sm text-muted-foreground">
                          {renderLogCell(log.before_summary)}
                        </TableCell>
                        <TableCell className="max-w-[320px] whitespace-pre-wrap text-sm text-muted-foreground">
                          {renderLogCell(log.after_summary)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          <DialogFooter className="shrink-0 border-t px-6 py-4">
            <Button variant="outline" onClick={() => setLogsOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!statusDialogMode} onOpenChange={(open) => !open && setStatusDialogMode(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {statusDialogMode === "mitigated" ? "Mitigar item" : statusDialogMode === "closed" ? "Concluir item" : "Reabrir item"}
            </DialogTitle>
            <DialogDescription>Informe o parecer técnico para registrar a alteração de status.</DialogDescription>
          </DialogHeader>
          <div>
            <div className="mb-2 text-sm font-medium">Parecer técnico *</div>
            <Textarea value={technicalOpinion} onChange={(e) => setTechnicalOpinion(e.target.value)} rows={6} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogMode(null)} disabled={setStatusMut.isPending}>
              Cancelar
            </Button>
            <Button onClick={confirmStatusChange} disabled={setStatusMut.isPending}>
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Excluir item do inventário"
        description="Essa ação remove definitivamente o item manual do inventário."
        onConfirm={confirmDelete}
        confirmLabel="Excluir item"
      />

      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
        title="Remover item do inventário"
        description="Essa ação remove o item convertido da lista do inventário atual."
        onConfirm={confirmRemove}
        confirmLabel="Remover item"
      />
    </div>
  );
}
